import {
  CheckBoxFieldv2,
  ExternalLink,
  LabraToolTipV2,
  ModalConfirmV2,
  ModalSizeVariants,
  NoteV2,
  SelectV4,
} from '@labrav/react-components'
import { DataType } from '@labrav/react-components/lib/@types/types/forms'
import { useAccountId } from '@labrav/common'
import _ from 'lodash'
import { Typography, SelectChangeEvent } from '@mui/material'
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCircleInfo,
  faMoneyBillWave,
} from '@fortawesome/pro-regular-svg-icons'
import { FormikProps } from 'formik'
import clsx from 'clsx'
import { useStyles } from './MultiCurrencyForm.styles'
import {
  currencyOptions,
  getCurrencyLabelForDisplay,
} from '../../../utils/getCurrencyLabelForDisplay'
import type { FlyOutListingData } from '../../../types/flyout'
import { useDispatch } from '../../../store'
import { patchSellerData } from '../../../modules/flyOutSyncs/action'

const GetEnableCurrencyNoteContent = () => {
  const { classes } = useStyles()
  return (
    <div className={classes.noteWrapper}>
      <Typography className={classes.importantText}>Important:</Typography>
      <ul className={classes.list}>
        <li>This only enables the currency in Labra.</li>
        <li>AMMP setup (bank account, KYC, disbursement) is still required.</li>
        <li>
          If incomplete, any offers or authorizations in this currency will
          fail. &nbsp;
          <ExternalLink
            className={classes.modalExternalLink}
            href={
              'https://docs.aws.amazon.com/marketplace/latest/userguide/provide-bank-information.html'
            }
          >
            Learn More
          </ExternalLink>
        </li>
      </ul>
    </div>
  )
}

const EnableCurrencyModal: React.FC<{
  open: boolean
  handleVisibility: () => void
  handleUpdateCurrencies: (currency: string | null) => void
  toBeEnabledCurrency: string | null
}> = ({
  open,
  handleVisibility,
  handleUpdateCurrencies,
  toBeEnabledCurrency,
}) => {
  const [checked, setChecked] = useState(false)
  const { classes } = useStyles()
  return (
    <ModalConfirmV2
      title={'Enable new currency'}
      content={
        <div className={classes.modalContent}>
          <Typography className={classes.modalHeader}>
            Before proceeding, confirm this currency is fully set up in your AWS
            Marketplace Management Portal (AMMP).
          </Typography>
          <NoteV2 description={<GetEnableCurrencyNoteContent />} />
          <CheckBoxFieldv2
            keyName="ConfirmCurrencySetup"
            title={
              <Typography className={classes.checkbox}>
                I confirm the selected currency is fully set up in AMMP
              </Typography>
            }
            value={checked}
            onChangeBox={(e: React.ChangeEvent<HTMLInputElement>) => {
              setChecked(e.target.checked)
            }}
          />
        </div>
      }
      acceptDisabled={!checked}
      disabledAcceptButtonMessage="Please confirm the currency is fully set up in AMMP"
      hideCancelButton={false}
      open={open}
      hideAcceptButton={false}
      titleAcceptButton={'Confirm'}
      onDecline={handleVisibility}
      onAccept={() => handleUpdateCurrencies(toBeEnabledCurrency)}
      variant={ModalSizeVariants.small}
      icon={
        <FontAwesomeIcon style={{ height: '14px' }} icon={faMoneyBillWave} />
      }
    />
  )
}

export const DisabledCurrencyComponent: React.FC<{
  currency: string
  handleModalVisibility: () => void
  setToBeEnabledCurrency: Dispatch<SetStateAction<string | null>>
}> = ({ currency, handleModalVisibility, setToBeEnabledCurrency }) => {
  const { classes } = useStyles()
  const handleButtonClick = () => {
    setToBeEnabledCurrency(currency)
    handleModalVisibility()
  }
  return (
    <>
      <Typography
        className={classes.disabledText}
        onClick={e => e.preventDefault()}
      >
        {getCurrencyLabelForDisplay(currency)}
      </Typography>

      <a
        className={classes.disabledCurrencyHyperlink}
        onClick={() => handleButtonClick()}
      >
        {' '}
        Enable
      </a>
    </>
  )
}

export type MultiCurrencyFormProps = {
  formik: FormikProps<Record<string, unknown>>
  keyName?: string
  disabled?: boolean
  flyoutSellerInfo: FlyOutListingData
  labraIsvId?: string
  headerText?: string
  subTitle?: string | JSX.Element | undefined
  infoText?: string
  selectInfoText?: string
  noteDescription?: string
}

export function MultiCurrencyForm({
  formik,
  keyName = 'currency',
  disabled,
  flyoutSellerInfo,
  labraIsvId,
  headerText,
  subTitle,
  infoText,
  selectInfoText = 'You will receive payment in the selected currency.',
  noteDescription,
}: MultiCurrencyFormProps) {
  const { values, setFieldValue } = formik
  const { classes } = useStyles()
  const enabledCurrencies = flyoutSellerInfo.currencies || []
  const dispatch = useDispatch()
  const accountIdFromHook = useAccountId({ product: 'flyout' })
  const accountId = labraIsvId ?? accountIdFromHook

  const [toBeEnabledCurrency, setToBeEnabledCurrency] = useState<string | null>(
    null
  )
  const [openCurrencyEnableModal, setOpenCurrencyEnableModal] = useState(false)
  const handleModalVisibility = () =>
    setOpenCurrencyEnableModal(!openCurrencyEnableModal)
  const handleCurrencyChange = useCallback(
    async (e: SelectChangeEvent) => {
      await setFieldValue(keyName, e.target.value)
    },
    [keyName, setFieldValue]
  )

  const sendSellerData = useCallback(
    async (currency: string | null) => {
      const selectedCurrency = currency
      if (selectedCurrency) {
        try {
          const data = {
            ...flyoutSellerInfo,
            currencies: flyoutSellerInfo.currencies
              ? [...flyoutSellerInfo.currencies, selectedCurrency]
              : [selectedCurrency],
          }
          await dispatch(patchSellerData(accountId as string, data))
        } catch (error) {
          console.error('Failed to update currencies:', error)
        }
      }
      handleModalVisibility()
    },
    [dispatch, accountId, flyoutSellerInfo]
  )

  const currencyOpts = useMemo(
    () =>
      Object.keys(currencyOptions).map(key => {
        const isEnabled = enabledCurrencies.includes(key)
        return {
          label: '',
          value: key,
          disabled: !isEnabled,
          disabledTooltip:
            'Complete setup in AWS Marketplace and enable this currency in Labra to use it.',
          menuItemAction: (
            <div className={classes.menuItems}>
              {!isEnabled ? (
                <DisabledCurrencyComponent
                  currency={key}
                  handleModalVisibility={handleModalVisibility}
                  setToBeEnabledCurrency={setToBeEnabledCurrency}
                />
              ) : (
                <Typography>{getCurrencyLabelForDisplay(key)}</Typography>
              )}
            </div>
          ),
        }
      }),
    [enabledCurrencies, classes.menuItems]
  )
  return (
    <>
      <EnableCurrencyModal
        open={openCurrencyEnableModal}
        handleVisibility={handleModalVisibility}
        handleUpdateCurrencies={sendSellerData}
        toBeEnabledCurrency={toBeEnabledCurrency}
      />
      <div className={classes.sectionContainer}>
        <div className={classes.subContainer}>
          <div className={classes.textContainer}>
            <Typography
              className={clsx(classes.sectionHeading, classes.fontColor)}
            >
              {headerText
                ? headerText
                : 'Set your transaction currency for this offer'}
            </Typography>
            {infoText && (
              <LabraToolTipV2 tooltipPlacement="top" description={infoText}>
                <FontAwesomeIcon
                  icon={faCircleInfo}
                  className={classes.toolTipIcon}
                />
              </LabraToolTipV2>
            )}
          </div>
          {subTitle ? (
            <>
              {noteDescription && <span>{noteDescription}</span>} {subTitle}
            </>
          ) : (
            <NoteV2
              description={
                <Typography className={classes.noteText}>
                  {noteDescription && <span>{noteDescription}</span>}
                  Currency must be fully set up in AMMP, otherwise offers and
                  authorizations using it will fail. &nbsp;
                  <ExternalLink
                    className={classes.externalLinkMain}
                    href={
                      'https://docs.aws.amazon.com/marketplace/latest/userguide/provide-bank-information.html'
                    }
                  >
                    Learn more
                  </ExternalLink>
                </Typography>
              }
            />
          )}
        </div>
        <div className={classes.selector}>
          <SelectV4
            data-testid="currency-select"
            field={{
              title: 'Select currency',
              placeHolder: 'Select currency',
              id: keyName,
              isRequired: false,
              humanReadableKey: keyName,
              isReadOnly: disabled,
              isCustom: false,
              dataType: 'enum' as DataType,
              options: currencyOpts,
              disableOpacity: true,
            }}
            value={getCurrencyLabelForDisplay(
              _.get(values, keyName, 'USD') as string
            )}
            onChange={handleCurrencyChange}
            showInsideInfoIcon={true}
            showInsideInfoIconTooltip={{
              title: '',
              content: selectInfoText,
            }}
          />
        </div>
      </div>
    </>
  )
}
